import type { AuthDictionary } from "./i18n.service";

/**
 * Traducción al inglés. Términos fiscales mexicanos (RFC, CAAT, CFDI, SAT) se
 * dejan sin traducir porque son identificadores oficiales y así aparecen en los
 * documentos; el resto usa la terminología logística de dexCarga.
 */
export const AUTH_EN: AuthDictionary = {
  // --- Shared ---------------------------------------------------------------
  'common.continue': 'Continue',
  'common.previous': 'Back',
  'common.cancel': 'Cancel',
  'common.back': 'Back',
  'common.required': 'This field is required',
  'common.optional': '(optional)',
  'common.emailInvalid': 'Enter a valid email address',
  'common.showPassword': 'Show password',
  'common.hidePassword': 'Hide password',
  'common.haveAccount': 'Already have an account?',
  'common.signInLink': 'Sign in →',

  // --- Roles ----------------------------------------------------------------
  'role.broker': 'Shipper',
  'role.shipper': 'Cargo owner',
  'role.carrier': 'Carrier',

  // --- Login ----------------------------------------------------------------
  'login.title': 'Sign in',
  'login.subtitle': 'Discover a more efficient way to run your shipments',
  'login.email': 'Email address',
  'login.password': 'Password',
  'login.remember': 'Remember me',
  'login.forgot': 'Forgot my password',
  'login.submit': 'Sign in',
  'login.noAccount': "Don't have an account?",
  'login.createAccount': 'Create account →',

  // --- Password recovery ----------------------------------------------------
  'forgot.title': 'Reset your password',
  'forgot.subtitle': "We'll email you a secure link to reset it.",
  'forgot.step.email': 'Email address',
  'forgot.step.emailHint': 'Get the link',
  'forgot.step.reset': 'Reset',
  'forgot.step.resetHint': 'Update your access',
  'forgot.sent.title': 'Link sent',
  'forgot.sent.body': 'Check your inbox. We sent the instructions to:',
  'forgot.backToLogin': 'Back to sign in',
  'forgot.email': 'Email address',
  'forgot.hint': 'The link is sent by email only. We will never ask for SMS verification.',
  'forgot.sending': 'Sending…',
  'forgot.send': 'Send link',
  'forgot.failed': "We couldn't send the link",

  // --- Signup: role selection -----------------------------------------------
  'registry.role.title': 'Choose your account type',
  'registry.role.subtitle': "Select the profile you'll create your account with.",
  'registry.role.label': 'Account type',

  // --- Signup: header and stepper -------------------------------------------
  'registry.title': 'Create your account',
  'registry.subtitle': 'Three quick steps to activate your dashboard.',
  'registry.step.personal': 'Personal details',
  'registry.step.personalHint': 'Your sign-in information',
  'registry.step.company': 'Company information',
  'registry.step.companyHint': 'Tax details and address',
  'registry.step.verification': 'Verification',
  'registry.step.verificationHint': 'Confirm your identity',

  // --- Signup: personal details ---------------------------------------------
  'registry.personal.firstName': 'First name',
  'registry.personal.lastName': 'Last name',
  'registry.personal.email': 'Email address',
  'registry.personal.phone': 'Phone',
  'registry.personal.phoneInvalid': 'Enter a valid phone number (7 to 15 digits)',
  'registry.personal.countryCode': 'Country code',
  'registry.personal.countryCodeSearch': 'Search country or code...',
  'registry.personal.password': 'Password',
  'registry.personal.confirmPassword': 'Repeat password',
  'registry.password.title': 'Your password must have:',
  'registry.password.length': 'At least 8 characters',
  'registry.password.upper': 'At least one uppercase letter',
  'registry.password.lower': 'At least one lowercase letter',
  'registry.password.digit': 'At least one number (0-9)',
  'registry.password.symbol': 'At least one symbol',
  'registry.password.match': 'Both passwords must match',

  // --- Signup: company ------------------------------------------------------
  'registry.company.section': 'Company details',
  'registry.company.rfc': 'RFC',
  'registry.company.validate': 'Validate',
  'registry.company.rfcAvailable': 'RFC available',
  'registry.company.rfcExisting': "Company already registered; we'll request access from its administrator.",
  'registry.company.rfcValidateFirst': 'Validate the RFC before continuing',
  'registry.company.rfcFormatValid': 'Valid RFC format',
  'registry.company.rfcFormatInvalid': 'Invalid RFC format',
  'registry.company.legalName': 'Legal name',
  'registry.company.legalNamePlaceholder': 'Your company name',
  'registry.company.taxRegime': 'Tax regime',
  'registry.company.taxRegimePlaceholder': 'Select a regime...',
  'registry.company.newCompanyHint': 'Enter the details to register a new company.',
  'registry.company.privacyNote': "We already have this company's tax address on file. For privacy reasons it is not shown and you don't need to enter it.",

  // --- Signup: tax address --------------------------------------------------
  'registry.address.section': 'Tax address',
  'registry.address.zipcode': 'ZIP code',
  'registry.address.zipcodePlaceholder': 'Enter your ZIP code (5 digits)',
  'registry.address.zipcodeHint': "Pick your neighborhood and we'll fill in the rest.",
  'registry.address.zipcodeNotFound': 'ZIP code not found in the SAT catalogue',
  'registry.address.catalogUnavailable': "We couldn't reach the SAT catalogue; please enter the details manually.",
  'registry.address.state': 'State',
  'registry.address.city': 'Municipality / City',
  'registry.address.neighborhood': 'Neighborhood',
  'registry.address.street': 'Street',
  'registry.address.exteriorNumber': 'Ext. no.',
  'registry.address.interiorNumber': 'Int. no.',
  'registry.address.selectPlaceholder': 'Select...',
  'registry.address.zipcodeFirst': 'Enter your ZIP code first',
  'registry.address.manualEntry': "The SAT catalogue has no data for your ZIP code; please enter it manually.",
  'registry.company.caat': 'CAAT',

  // --- Signup: verification and submit --------------------------------------
  'registry.verification.intro': "We'll send a confirmation link to verify your identity and activate the account.",
  'registry.verification.boxTitle': 'Email verification',
  'registry.verification.boxBody': "When you create your account we'll send a confirmation link to:",
  'registry.verification.boxFooter': 'You must open the link to activate your account before signing in.',
  'registry.verification.submit': 'Create account',
  'registry.signup.pendingTitle': 'Creating your account',
  'registry.signup.pendingBody': 'Please wait a moment…',
  'registry.signup.successTitle': 'Registration complete!',
  'registry.signup.successBody': "Check your email to confirm your account; if the company already exists, we'll notify you once it's approved.",
  'registry.signup.successCta': 'Back to start',
  'registry.signup.errorTitle': "We couldn't complete the registration",
  'registry.signup.errorCta': 'Go back',

  // --- Signup: toasts -------------------------------------------------------
  'toast.personalIncomplete': 'Complete your personal details',
  'toast.passwordWeak': "Your password doesn't meet the requirements",
  'toast.rfcInvalid': 'Enter a valid RFC',
  'toast.rfcValidateFirst': 'Validate the RFC before continuing',
  'toast.companyIncomplete': 'Complete the company information',
  'toast.zipcodeInvalid': 'The ZIP code must be 5 digits',
  'toast.addressIncomplete': 'Complete the tax address',
  'toast.rfcAvailable': 'RFC available',
  'toast.rfcExisting': "Company already registered; we'll request access from its administrator.",
  'toast.legalAccepted': 'Terms and privacy notice accepted',
  'toast.signupFailed': "We couldn't complete the registration",

  // --- Sign-in notices ------------------------------------------------------
  'toast.loginFailed': "We couldn't sign you in",
  'toast.roleNotAuthorized': "This profile can't sign in here",
  'toast.roleNotAuthorizedDetail': 'Driver accounts are used from the mobile app.',

  // --- Legal consent --------------------------------------------------------
  'legal.terms.eyebrow': 'Step 1 of 2',
  'legal.terms.title': 'Terms of service',
  'legal.terms.description': 'Before continuing we need you to review and accept the terms that govern the use of dexCarga.',
  'legal.terms.link': 'Read the Terms of service',
  'legal.terms.checkbox': 'I have read and accept the Terms of service.',
  'legal.terms.point1': "How you can use the platform and your company's accounts.",
  'legal.terms.point2': "Each party's responsibilities in coordinating shipments.",
  'legal.terms.point3': 'Contracting, term and cancellation conditions.',
  'legal.privacy.eyebrow': 'Step 2 of 2',
  'legal.privacy.title': 'Privacy notice',
  'legal.privacy.description': 'We also need your consent regarding the processing of your personal data.',
  'legal.privacy.link': 'Read the Privacy notice',
  'legal.privacy.checkbox': 'I have read and accept the Privacy notice.',
  'legal.privacy.point1': 'Which personal and tax data we collect during signup.',
  'legal.privacy.point2': 'What we use it for and who we may share it with.',
  'legal.privacy.point3': 'How to exercise your ARCO rights over your information.',
  'legal.privacy.point4': 'How we record your use of the platform —screens, clicks and failures, never what you type— to support you and improve it, for how long, and how to object.',
  'legal.acceptAndContinue': 'Accept and continue',
  'legal.acceptAndFinish': 'Accept and finish',
  'legal.close': 'Close',

  // --- Language selector ----------------------------------------------------
  'lang.es': 'México - Español',
  'lang.en': 'USA - English',
  'lang.label': 'Language',
};
